/**
 * "None of these times work for us" — the day × time-of-day grid a family ticks
 * when no running class suits them (Joshua, 10 Sep).
 *
 * Bands are wall-clock in the zone the page displays class times in; the request
 * records that zone beside the slots.
 *
 * ⚠️ NO TIME COMPARISONS HERE. `weekly-slot-overlap.audit.test.ts` scans
 * `src/lib` for hand-rolled `HH:MM` arithmetic, and none is needed: adjacent
 * bands merge by their POSITION in `ANOTHER_TIME_BANDS`, and the validator checks
 * a day's slots against the canonical shapes by lookup.
 */

export const ANOTHER_TIME_BANDS = [
  { key: 'morning', start: '08:00', end: '12:00' },
  { key: 'afternoon', start: '12:00', end: '17:00' },
  { key: 'evening', start: '17:00', end: '21:00' },
] as const;

export type AnotherTimeBandKey = (typeof ANOTHER_TIME_BANDS)[number]['key'];

/**
 * The order the grid shows the week in, Monday first — a list of `day_of_week`
 * VALUES (0 = Sunday, as everywhere else in the repo), not a label array
 * indexed by `day_of_week`. The 0=Sunday audit governs those label arrays, and
 * this is deliberately not named like one: reading it as `DAYS[day]` would be
 * wrong, which is exactly the mistake that audit exists to catch.
 */
export const ANOTHER_TIME_WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0] as const;

export type AnotherTimeSlot = { day_of_week: number; start_time: string; end_time: string };

/**
 * The slots a ticked grid stands for: per ticked day (display order), the ticked
 * bands with adjacent ones merged — Morning + Afternoon is one 08:00–17:00
 * window, not two slots that happen to touch. Morning + Evening stays two.
 *
 * Unknown days or band keys are ignored. Empty when either input is.
 */
export function buildAnotherTimeSlots(
  days: readonly number[],
  bands: readonly string[],
): AnotherTimeSlot[] {
  const daySet = new Set(days);
  const bandSet = new Set(bands);
  const tickedIndexes = ANOTHER_TIME_BANDS.map((b, i) => (bandSet.has(b.key) ? i : -1)).filter(
    (i) => i >= 0,
  );
  if (tickedIndexes.length === 0) return [];

  // Runs of consecutive band indexes.
  const runs: Array<{ start: string; end: string }> = [];
  let runStart = tickedIndexes[0];
  let prev = tickedIndexes[0];
  for (const i of [...tickedIndexes.slice(1), -1]) {
    if (i === prev + 1) {
      prev = i;
      continue;
    }
    runs.push({ start: ANOTHER_TIME_BANDS[runStart].start, end: ANOTHER_TIME_BANDS[prev].end });
    runStart = i;
    prev = i;
  }

  const slots: AnotherTimeSlot[] = [];
  for (const day of ANOTHER_TIME_WEEK_ORDER) {
    if (!daySet.has(day)) continue;
    for (const run of runs) {
      slots.push({ day_of_week: day, start_time: run.start, end_time: run.end });
    }
  }
  return slots;
}

/** Most slots a grid can produce: seven days × Morning and Evening apart. */
export const ANOTHER_TIME_MAX_SLOTS = 14;

/**
 * Every shape ONE day's slots may take, as sorted `start-end` lists — exactly
 * what `buildAnotherTimeSlots` can produce for one day. The validator refuses a
 * day whose slots are not one of these, so a flagged request carries nothing the
 * grid could not have sent.
 */
export const ANOTHER_TIME_DAY_SHAPES: readonly (readonly string[])[] = [
  ['08:00-12:00'],
  ['12:00-17:00'],
  ['17:00-21:00'],
  ['08:00-17:00'],
  ['12:00-21:00'],
  ['08:00-21:00'],
  ['08:00-12:00', '17:00-21:00'],
];

/** Is this sorted `start-end` list one of the seven canonical day shapes? */
export function isAnotherTimeDayShape(sortedRanges: readonly string[]): boolean {
  const key = sortedRanges.join('|');
  return ANOTHER_TIME_DAY_SHAPES.some((shape) => shape.join('|') === key);
}
